import { ErrorHandler, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Manejador global de errores del frontend.
 *
 * A PRUEBA DE FALLOS: mantiene el comportamiento normal (registra en consola) y,
 * de forma OPCIONAL, reenvía el error al Core API (`/support/client-error`), que a
 * su vez lo manda a la mesa de ayuda SOLO si la ingesta está configurada. Si el
 * endpoint no existe o falla, se traga el error: nunca rompe la app.
 *
 * El token de la mesa de ayuda vive únicamente en el backend: aquí no hay
 * secretos ni configuración; por eso mover de servidor no afecta al frontend.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly endpoint = `${environment.apiUrl}/support/client-error`;

  // Anti-flood: no repetir el mismo error en una ventana y tope por minuto.
  private readonly recent = new Map<string, number>();
  private sentWindow: number[] = [];
  private static readonly DEDUPE_MS = 60_000;
  private static readonly MAX_PER_MIN = 20;

  handleError(error: unknown): void {
    // 1) Comportamiento estándar de Angular: dejar el error en consola.
    // eslint-disable-next-line no-console
    console.error(error);
    // 2) Telemetría opcional, totalmente aislada.
    try {
      this.send(error);
    } catch {
      /* la telemetría nunca puede afectar a la app */
    }
  }

  private send(error: unknown): void {
    const err = error as {
      message?: unknown;
      stack?: unknown;
      name?: string;
      status?: number;
      originRoute?: string;
    };
    const message = String(err?.message ?? error ?? '').trim();
    if (!message || message === 'undefined' || message === 'null') return;

    // Errores HTTP 5xx: el backend ya los captura; evitamos duplicar ruido.
    if (err?.name === 'HttpErrorResponse' && typeof err.status === 'number' && err.status >= 500) {
      return;
    }

    // 401/403 no son defectos: son sesion expirada o falta de permiso, y la app
    // ya los atiende (interceptor -> lockscreen). Levantaban tickets con
    // prioridad Alta cada vez que a alguien se le vencia la sesion (EA3AF9,
    // 21B677). No se reportan.
    if (err?.name === 'HttpErrorResponse' && (err.status === 401 || err.status === 403)) {
      return;
    }

    const stack = typeof err?.stack === 'string' ? err.stack : undefined;
    const now = Date.now();
    const key = (message + '|' + (stack?.split('\n')[1] ?? '')).slice(0, 200);
    const last = this.recent.get(key);
    if (last && now - last < GlobalErrorHandler.DEDUPE_MS) return;

    this.sentWindow = this.sentWindow.filter((t) => now - t < 60_000);
    if (this.sentWindow.length >= GlobalErrorHandler.MAX_PER_MIN) return;
    this.recent.set(key, now);
    this.sentWindow.push(now);
    if (this.recent.size > 300) {
      for (const [k, t] of this.recent) {
        if (now - t > GlobalErrorHandler.DEDUPE_MS) this.recent.delete(k);
      }
    }

    // Si el error viene de un 401 que ya redirigió a /lockscreen, el interceptor
    // adjuntó la pantalla original (originRoute) — la preferimos sobre location.*,
    // que para entonces ya apunta al lockscreen y no a donde ocurrió el error.
    const originRoute = typeof err?.originRoute === 'string' ? err.originRoute : undefined;
    const route =
      originRoute ?? (typeof location !== 'undefined' ? location.pathname + location.search : undefined);
    const url =
      originRoute && typeof location !== 'undefined'
        ? location.origin + originRoute
        : typeof location !== 'undefined'
          ? location.href
          : undefined;

    const payload = {
      message: message.slice(0, 2000),
      stack: stack?.slice(0, 8000),
      url,
      route,
      service: 'belatz-core-app',
      occurredAt: new Date(now).toISOString(),
    };

    // fire-and-forget: fetch directo (sin interceptores/DI) con keepalive; swallow.
    try {
      void fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {
        /* sin red o endpoint viejo: ignorar */
      });
    } catch {
      /* noop */
    }
  }
}
